import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "@/App.css";
import SignalTester from "@/SignalTester";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCLAIMER =
  "Signal Wrapper provides deterministic prioritization signals based on available " +
  "transaction data only. Scores are not legal determinations, fraud assessments, " +
  "regulatory compliance certifications, or financial advice. Results should be reviewed " +
  "by qualified personnel before any procurement decision is made. Methodology version is " +
  "recorded with every result for reproducibility and audit purposes.";

const fmt = (ts) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

const pillClass = (status) => {
  if (status === "HIGH_PRIORITY") return "sw-pill-high";
  if (status === "REVIEW") return "sw-pill-review";
  if (status === "WATCH") return "sw-pill-watch";
  return "sw-pill-normal";
};

const OwnerGate = ({ onUnlock }) => {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{8}$/.test(pin.trim())) {
      setErr("Enter the 8-digit owner PIN.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/owner-access`, { pin: pin.trim() });
      if (res.data?.valid) onUnlock();
      else setErr("Incorrect owner PIN.");
    } catch {
      setErr("Couldn't verify the PIN. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sw-landing sw-portal-gate-wrap">
      <form className="sw-gate" onSubmit={submit} data-testid="owner-pin-form" noValidate>
        <span className="sw-gate-label" data-testid="owner-portal-label">Owner Portal</span>
        <p className="sw-gate-copy">Enter your 8-digit owner PIN to access the portal.</p>
        <div className="sw-gate-row">
          <input type="text" inputMode="numeric" maxLength={8} className="sw-input sw-pin"
            placeholder="••••••••" value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            data-testid="owner-pin-input" aria-label="Owner PIN" />
          <button type="submit" className="sw-btn sw-btn-primary" disabled={loading}
            data-testid="owner-pin-submit">
            {loading ? "Checking…" : "Enter portal"}
          </button>
        </div>
        {err ? <p className="sw-error" data-testid="owner-pin-error">{err}</p> : null}
      </form>
    </div>
  );
};

const EventsTable = ({ events }) => (
  <div className="sw-table-wrap" data-testid="portal-signal-events">
    <table className="sw-table">
      <thead>
        <tr><th>Supplier</th><th>Priority score</th><th>Status</th><th>Timestamp</th></tr>
      </thead>
      <tbody>
        {events.length === 0 ? (
          <tr><td colSpan="4" className="sw-empty">No signal events yet.</td></tr>
        ) : (
          events.map((e) => (
            <tr key={e.event_id} data-testid="portal-event-row">
              <td>{e.supplier_name || e.supplier_id}</td>
              <td>{e.priority_score}</td>
              <td>
                <span className={`sw-pill ${pillClass(e.priority_status)}`}>
                  {String(e.priority_status).replace(/_/g, " ")}
                </span>
              </td>
              <td>{fmt(e.created_at)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const RequestsTable = ({ requests }) => (
  <div className="sw-table-wrap" data-testid="portal-access-requests">
    <table className="sw-table">
      <thead>
        <tr><th>Email</th><th>Company</th><th>Use case</th></tr>
      </thead>
      <tbody>
        {requests.length === 0 ? (
          <tr><td colSpan="3" className="sw-empty">No access requests yet.</td></tr>
        ) : (
          requests.map((r) => (
            <tr key={r.id} data-testid="portal-request-row">
              <td>{r.email}</td>
              <td>{r.company || "—"}</td>
              <td>{r.use_case || "—"}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const usePortalData = () => {
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);

  const load = useCallback(async () => {
    try {
      const [ev, rq] = await Promise.all([
        axios.get(`${API}/portal/signal-events`),
        axios.get(`${API}/access-requests`),
      ]);
      setEvents(ev.data || []);
      setRequests(rq.data || []);
    } catch (error) {
      console.error("Portal: failed to load data:", error);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 6000); // auto-refresh without a reload
    return () => clearInterval(timer);
  }, [load]);

  return { events, requests };
};

const PortalContent = () => {
  const { events, requests } = usePortalData();

  return (
    <div className="sw-landing" data-testid="owner-portal">
      <section className="sw-how">
        <div className="sw-section-head">
          <span className="sw-kicker">Owner Portal · live</span>
          <h2 className="sw-h2">Operations dashboard.</h2>
        </div>

        <div className="sw-portal-row-head">
          <h3 className="sw-portal-h3">Signal events</h3>
          <a className="sw-btn sw-btn-ghost sw-btn-sm" href={`${API}/portal/signal-events.csv`}
            data-testid="export-events-csv">Export CSV</a>
        </div>
        <EventsTable events={events} />

        <div className="sw-portal-row-head">
          <h3 className="sw-portal-h3">Access requests</h3>
          <a className="sw-btn sw-btn-ghost sw-btn-sm" href={`${API}/portal/access-requests.csv`}
            data-testid="export-requests-csv">Export CSV</a>
        </div>
        <RequestsTable requests={requests} />

        <h3 className="sw-portal-h3">Live signal tester</h3>
        <SignalTester idPrefix="portal-tester" />

        <p className="sw-disclaimer" data-testid="portal-disclaimer">{DISCLAIMER}</p>
      </section>
    </div>
  );
};

export default function Portal() {
  const [unlocked, setUnlocked] = useState(false);
  return unlocked ? <PortalContent /> : <OwnerGate onUnlock={() => setUnlocked(true)} />;
}
